import { ReactNode } from 'react';
import { useMediaQuery } from '@providers/MediaQueryProvider';
import { Link } from '@tanstack/react-router';

import { cx } from '@libs/classnames';
import { HorizontalScroll } from '@ui/components/HorizontalScroll';
import { SlimArrowButton } from '@ui/components/SlimArrowButton';
import { FeatherIcon } from '@ui/icons/FeatherIcon';

import styles from './Layout.module.css';

export type Props = {
  disabled?: boolean;
  board: ReactNode;
  avatar?: ReactNode;
  header?: ReactNode;
  scoresheet: ReactNode;
  player1?: ReactNode;
  player2?: ReactNode;
  actions?: ReactNode;
  games?: ReactNode;
  backLink?: string;
  backLinkIsLogo?: boolean;
};

export function Layout(props: Props) {
  const device = useMediaQuery();
  return device === 'desktop' ? <DesktopLayout {...props} /> : <MobileLayout {...props} />;
}

/*
    ____,-------------------------------,____
    \   |             Layouts           |   /
    /___|-------------------------------|___\
*/

function DesktopLayout({
  disabled,
  board,
  avatar,
  header,
  scoresheet,
  player1,
  player2,
  actions,
  games,
  backLink,
  backLinkIsLogo,
}: Props) {
  const hasPlayers = player1 && player2;

  return (
    <div className={cx(styles.layoutWrapper, { [styles.is_disabled]: disabled })}>
      <div className={styles.layout} translate="no">
        {avatar && <div className={styles.avatar}>{avatar}</div>}
        <div className={styles.board}>
          {backLink && (
            <Link to={backLink} className={styles.desktopBackBtn}>
              {backLinkIsLogo ? (
                <FeatherIcon className={styles.logo} />
              ) : (
                <SlimArrowButton direction="left" size="24" colorPreset="currentColor" />
              )}
            </Link>
          )}
          {board}
        </div>
        <div className={styles.sidebar}>
          <div className={styles.header}>{header}</div>
          <div className={styles.stack}>
            {hasPlayers && <div className={styles.player}>{player1}</div>}
            <div className={styles.scoresheet}>{scoresheet}</div>
            {hasPlayers && <div className={styles.player}>{player2}</div>}
          </div>
          <div className={styles.footer}>{actions}</div>
        </div>
      </div>
      {games && <div className={styles.games}>{games}</div>}
    </div>
  );
}

function MobileLayout({
  disabled,
  board,
  avatar,
  header,
  scoresheet,
  player1,
  player2,
  actions,
  games,
  backLink: _backLink,
  backLinkIsLogo: _backLinkIsLogo,
}: Props) {
  const hasPlayers = player1 && player2;

  return (
    <div className={cx(styles.layoutMobile, { [styles.is_disabled]: disabled })}>
      {avatar && <div className={styles.avatar}>{avatar}</div>}
      {header}
      <div className={styles.stack}>
        {hasPlayers && <div className={styles.player}>{player1}</div>}
        <div className={styles.board}>{board}</div>
        {hasPlayers && <div className={styles.player}>{player2}</div>}
      </div>
      {games}
      <div className={styles.actionsMobileWrapper}>
        <HorizontalScroll className={styles.actionsMobile} fullWidth>
          {actions}
        </HorizontalScroll>
      </div>
      <div className={styles.scoresheetMobile}>{scoresheet}</div>
    </div>
  );
}
